/**
 * Regras de PII: o que não pode sair do browser.
 *
 * É a primeira barreira; a segunda é o pipeline de ingest server-side no
 * OpenObserve, que pega o que escapar daqui. Falha fechado de propósito: na
 * dúvida, redige. Perder contexto de log é barato; vazar dado pessoal ou
 * financeiro para um terceiro não é. Antes de "simplificar" uma regex ou tirar
 * uma palavra da denylist, rode `pii.spec.js` e pense no que passa a vazar.
 *
 * Ver docs/observabilidade-e-logging.md.
 */

export const REDACTED = '[REDACTED]'

const MAX_SCRUB_DEPTH = 4

// Só parâmetros `chave=valor`: rotas por hash como `#/painel` passam.
const URL_PARAMS = /[?#][^\s'"()<>]*=[^\s'"()<>]*/g
const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g
// CNPJ antes de CPF: senão a regex de CPF come um pedaço do CNPJ.
const CNPJ = /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g
const CPF = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g
const PHONE = /(?:\(?\b\d{2}\)?\s?)?\b9?\d{4}[-\s]\d{4}\b/g
// Telefone cru; pega outros números longos de propósito.
const LONG_DIGITS = /\b\d{10,11}\b/g

/** @param {unknown} value */
export function redactString(value) {
  if (typeof value !== 'string') return value
  return value
    .replace(URL_PARAMS, '')
    .replace(EMAIL, REDACTED)
    .replace(CNPJ, REDACTED)
    .replace(CPF, REDACTED)
    .replace(PHONE, REDACTED)
    .replace(LONG_DIGITS, REDACTED)
}

/**
 * Remove query string e fragmento — qualquer fragmento, inclusive rota por hash
 * (`#/painel`). O link do liveness leva o token em `#token=`, e o RUM monta
 * `view.url` a partir de `location.href`: cortar tudo é o mais seguro.
 * @param {unknown} url
 */
export function stripUrl(url) {
  if (typeof url !== 'string') return url
  const i = url.search(/[?#]/)
  return i === -1 ? url : url.slice(0, i)
}

// Comparação por palavra da chave, não por substring (`contador` não casa com `conta`).
const SENSITIVE_WORDS = new Set([
  'cpf',
  'cnpj',
  'rg',
  'document',
  'documento',
  'otp',
  'senha',
  'password',
  'token',
  'secret',
  'authorization',
  'cvv',
  'card',
  'cartao',
  'pix',
  'email',
  'telefone',
  'phone',
  'celular',
  'nome',
  'name',
  'endereco',
  'address',
  'cep',
  'rua',
  'logradouro',
  'agencia',
  'agency',
  'liveness',
])

// Sensíveis só sozinhas ou com qualificador: `account`, `api_key` sim; `accountType` não.
const AMBIGUOUS_WORDS = new Set(['account', 'conta', 'access', 'refresh', 'key'])
const QUALIFIER_WORDS = new Set(['number', 'numero', 'digit', 'digito', 'api'])

/** `pixKey` / `pix_key` / `pix-key` → ['pix', 'key'], sem acento. */
function keyWords(key) {
  return key
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

/** @param {string} key */
export function isSensitiveKey(key) {
  const words = keyWords(key)
  if (words.some(word => SENSITIVE_WORDS.has(word))) return true

  const ambiguous = words.some(word => AMBIGUOUS_WORDS.has(word))
  if (!ambiguous) return false
  return words.length === 1 || words.some(word => QUALIFIER_WORDS.has(word))
}

/**
 * Reduz um `Error`/AxiosError ao mínimo e descarta `config`/`request`/`response`,
 * onde o payload da requisição viaja em claro.
 * @param {Error & { response?: { status?: number }, config?: { url?: string } }} error
 */
export function normalizeError(error) {
  return {
    name: error.name,
    message: redactString(error.message),
    status: error.response?.status,
    url: stripUrl(error.config?.url),
  }
}

/** @param {unknown} value */
export function scrub(value, seen = new WeakSet(), depth = 0) {
  if (value == null) return value
  if (typeof value === 'string') return redactString(value)
  if (typeof value !== 'object') return value
  if (value instanceof Error) return normalizeError(value)
  if (seen.has(value)) return '[Circular]'
  if (depth >= MAX_SCRUB_DEPTH) return '[Truncated]'
  seen.add(value)

  if (Array.isArray(value)) return value.map(item => scrub(item, seen, depth + 1))

  const out = {}
  for (const [key, val] of Object.entries(value)) {
    out[key] = isSensitiveKey(key) ? REDACTED : scrub(val, seen, depth + 1)
  }
  return out
}
